"use client"
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { getSearchByCreatedAt } from "@lib/api/user"
import { filtersState } from "@utils/FiltersState"
import { texts } from "@utils/textMessages"
import { store } from "@utils/store"
import { waiter } from "@utils/index"
import { openModal } from "@components/Layout/components/LoginModal"
import { usePathname, useRouter } from "next/navigation"
import { CKEditor } from "../../CKEditor"
import { message } from "@components/Message/message"

const dbSortToTable = {
    asc: "ascend",
    desc: "descend"
}

const filterActions = {
    setFilter: "setFilter",
    clear: "clear",
    setInitialFilter: "setInitialFilter"
}

const changeFilterFlow = async (action, setLoading, data, pathname) => {
    const search = new URLSearchParams(location.search)
    const resorts = search.get("resorts")
    const filter = search.get("filter")
    async function loadUserFilter(filter, search) {
        const resp = search && search.search_query ? search : await getSearchByCreatedAt(filter)
        filtersState.clear()
        const { sort, where } = resp?.search_query
        if (sort) {
            const [column, type] = sort.split(":")
            filtersState.setSort(
                {
                    value: sort,
                    column,
                    order: dbSortToTable[type]
                },
                pathname
            )
        } else {
            filtersState.setSort({}, pathname)
        }
        filtersState.setFilter(where, pathname, resp?.created_at)
    }
    if (filter) {
        search.delete("filter")
        const searchValue = search.toString()
        window.history.replaceState(null, "", pathname + (searchValue ? "?" + searchValue : ""))
    }

    switch (action) {
        case filterActions.setInitialFilter: {
            if (filter) {
                filtersState.savedSearch = filter
                const searches = store.get(store.k.searches) || []
                const search = searches.find((i) => i.createdAt === filter)
                try {
                    await loadUserFilter(filter, search)
                } catch (err) {
                    console.warn(err)
                    if (err.statusCode === 401) {
                        openModal("/listings?filter=" + filter)
                    } else {
                        message.warning(texts.listingFilterCantBeLoaded)
                        filtersState.savedSearch = null
                    }
                } finally {
                    setLoading(true)
                    await waiter(100)
                    setLoading(false)
                }
                return
            } else if (resorts) {
                // TODO
                const { status_nin } = filtersState.getFilter(pathname)
                filtersState.savedSearch = null
                filtersState.setFilter(
                    {
                        status_nin,
                        resort: {
                            short_name_in: resorts.split(",")
                        }
                    },
                    pathname
                )
                setLoading(true)
                await waiter(100)
                setLoading(false)
                return
            }
            break
        }
        case filterActions.setFilter: {
            filtersState.savedSearch = data
            const searches = store.get(store.k.searches) || []
            const search = searches.find((i) => i.createdAt === data)
            try {
                await loadUserFilter(data, search)
            } catch (err) {
                console.warn(err)
                if (err.statusCode === 401) {
                    openModal()
                } else {
                    message.warning(texts.listingFilterCantBeLoaded)
                    filtersState.savedSearch = null
                }
            } finally {
                setLoading(true)
                await waiter(100)
                setLoading(false)
            }
            return
        }
        case filterActions.clear: {
            filtersState.clear(pathname)
            filtersState.setSort({}, pathname)
            filtersState.savedSearch = null
            setLoading(true)
            window.history.replaceState(null, "", location.pathname)
            setLoading(false)
            return
        }
    }
    setLoading(false)
}

const ListingContext = createContext(() => {})

export function useFilters() {
    return useContext(ListingContext)
}

let lastURL = ""
const title = "Listings"
const description = "Explore the listings and choose your ideal contract. Choose from the largest collection of DVC listings on the internet."
export function ListingsContainer(Comp) {
    return function WithFilters(props) {
        const pathname = usePathname()
        const router = useRouter()
        const ref = useRef(pathname)
        const [loading, setLoading] = useState(process.browser ? lastURL !== window.location.href : true)
        useEffect(() => {
            changeFilterFlow(filterActions.setInitialFilter, setLoading, undefined, pathname)
            return () => {
                lastURL = location.href
            }
        }, [pathname])

        useEffect(() => {
            // handle redirect from resort/listings to listings
            if (pathname !== ref.current) {
                ref.current = pathname
                ;(async function () {
                    setLoading(true)
                    await waiter(300)
                    setLoading(false)
                })()
            }
        }, [pathname])

        const changeFilter = useCallback(
            (data) => {
                if (data && data !== "0") {
                    changeFilterFlow(filterActions.setFilter, setLoading, data, pathname)
                } else {
                    changeFilterFlow(filterActions.clear, setLoading, undefined, pathname)
                }
            },
            [pathname]
        )
        const seo = props.seo
        const desc = seo.content ? <CKEditor content={seo.content} /> : <p>{description}</p>
        const h1 = seo.content_h1 || seo.h1 || title
        return (
            <ListingContext.Provider value={changeFilter}>
                {/*                {loading && (
                    <>
                        <div className={styles.spinContainer}>
                            <Spin spinning size={"large"} className={styles.globalSpinner} />
                        </div>
                    </>
                )}*/}
                <Comp {...props} router={router} pathname={pathname} description={desc} title={h1} loading={loading} changeFilter={changeFilter} />
            </ListingContext.Provider>
        )
    }
}
